import { getTagFromNode, useTranslation } from '@msdk/common'
import {
  ControlValidationType,
  FormFieldDefinition,
  FormOpenMode,
  StringFieldDefinition,
  StringValue,
} from '@msdk/definitions'
import {
  FormField,
  FormFieldLabel,
  FormHelperText,
  IconSelector,
  InputAdornment,
  TextAreaWithLabel,
  TextInput,
} from '@msdk/ui'
import React, { ChangeEvent, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { nodeHasFlag } from '../../../utils'
import { ValidationMessages } from '../../ValidationMessages'

const DEFAULT_MIN_ROWS = 1
const DEFAULT_MAX_ROWS = 3

export function StringField({ node, onChange, fieldArgs }: FieldProps) {
  const fieldDef = node.value.fieldDefinition as StringFieldDefinition
  const formDef = node.value.formDefinition as FormFieldDefinition
  const fieldLabel = useFieldLabel(fieldDef, formDef)
  const t = useTranslation()
  const { moduleName } = useParams()
  const [value, setValue] = useFieldValue(node, onChange)
  const [shrink, setShrink] = useState(!value)

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fieldDef.entityName)

  const onFieldChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const fieldValue = newValue !== '' ? new StringValue(newValue) : null
    node.value.isDirty = true
    setValue(fieldValue)
  }

  const isReadOnly = node.value.state === 'Readonly'
  const severity = useFieldSeverity(fieldDef, node)

  const placeholder =
    fieldArgs?.showLabel || fieldArgs?.showPlaceholder
      ? fieldArgs?.useLabelAsPlaceholder
        ? fieldLabel
        : t('CMPS_TYPE_HERE_PLACEHOLDER', 'Type here')
      : undefined

  const charCounter = !isReadOnly && fieldDef.showCharCounter && fieldDef.maxLength > 0 && (
    <InputAdornment position="end">
      <FormHelperText>
        {value ? (value.value as string).length : 0}/{fieldDef.maxLength}
      </FormHelperText>
    </InputAdornment>
  )

  return (
    <FormField
      $hasPrefix={!!fieldDef.prefixIcon}
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fieldDef, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      {!fieldDef.isComment && (
        <>
          {fieldArgs?.showLabel && (
            <FormFieldLabel
              shrink={fieldDef.prefixIcon ? shrink : undefined}
              filterField={fieldArgs?.filterField}
              hasEndAdornment={!!charCounter}
            >
              {fieldLabel}
            </FormFieldLabel>
          )}
          <TextInput
            label={fieldArgs?.showLabel ? (value || shrink ? fieldLabel : undefined) : undefined}
            inputMode="text"
            size="small"
            onFocus={() => setShrink(true)}
            onBlur={(e) => setShrink(!!e.target.value)}
            placeholder={placeholder}
            disabled={isReadOnly}
            onChange={onFieldChange}
            value={value ? value.toStringForView() : ''}
            endAdornment={charCounter}
            startAdornment={
              fieldDef.prefixIcon ? (
                <InputAdornment position="start">
                  <IconSelector iconName={fieldDef.prefixIcon}></IconSelector>
                </InputAdornment>
              ) : undefined
            }
          />
        </>
      )}
      {fieldDef.isComment && (
        <TextAreaWithLabel
          maxRows={fieldDef.rows > 0 ? fieldDef.rows : DEFAULT_MAX_ROWS}
          minRows={fieldDef.rows > 0 ? fieldDef.rows : DEFAULT_MIN_ROWS}
          placeholder={placeholder}
          inputMode="text"
          disabled={isReadOnly}
          onChange={onFieldChange}
          value={value ? value.toStringForView() : ''}
          label={fieldArgs?.showLabel ? fieldLabel : ''}
        />
      )}

      <ValidationMessages messages={node.value.validationMessages} fieldDef={fieldDef} node={node} />
    </FormField>
  )
}
